'use client';

import { H2, P, H2Small, PLarge } from './StyleConstants';
import { useRef } from 'react';
import { Container } from '..';
import gsap from 'gsap';
import { CustomImage as Image } from 'components/shared';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from '../../hooks/animation/useGsapContext';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import styles from './R1WithM1Section.module.css';

export const R1WithM1Section = ({
  className,
  title,
  description,
  mblimageL,
  mblimageR,
  titleLarge,
  textColor,
}: {
  className?: string;
  title?: string | React.ReactNode;
  description?: string;
  mblimageL: string;
  mblimageR: string;
  titleLarge?: boolean;
  textColor?: string;
}) => {
  const container = useRef<HTMLDivElement>(null);
    const imageL = useRef<HTMLImageElement>(null);
    const imageR = useRef<HTMLImageElement>(null);
    const gsapContext = useGsapContext();
    const { add } = useMasterTimeline();
    const { width } = useWindowSize();

    const animationInit = () => {
      if (!container.current || !imageL.current || !imageR.current) return;
      gsapContext.add(() => {
        const tl = gsap.timeline({
          defaults: { duration: 1.5 },
          scrollTrigger: {
            trigger: container.current,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1,
            invalidateOnRefresh: true,
          },
        });

        width > 720
          ? tl
              .add('start')
              .fromTo(
                imageL.current,
                {
                  y: 0,
                },
                {
                  y: -80,
                },
                'start',
              )
              .fromTo(
                imageR.current,
                {
                  y: 0,
                },
                {
                  y: 80,
                },
                'start',
              )
          : tl
              .add('start')
              .fromTo(
                imageL.current,
                {
                  y: 0,
                },
                {
                  y: -40,
                },
                'start',
              )
              .fromTo(
                imageR.current,
                {
                  y: 0,
                },
                {
                  y: 40,
                },
                'start',
              );

        add(tl, 'r1-with-m1-section', 6);
      });
    };

    useIsomorphicLayoutEffect(() => {
      const animTimer = setTimeout(animationInit, 100);
      return () => {
        if (animTimer) clearTimeout(animTimer);
      };
    }, []);

    return (
      <section
        className={className ? `${styles.root} ${className}` : styles.root}
        ref={container}
        style={{ color: textColor }}
      >
        <Container>
          <div className={styles.sticky}>
            {titleLarge ? (
              <div className={styles.textContent1}>
                <H2>{title}</H2>
                <P>{description}</P>
              </div>
            ) : (
              <div className={styles.textContent1}>
                <H2Small>{title}</H2Small>
                <PLarge>{description}</PLarge>
              </div>
            )}
            <div className={styles.imgContainer}>
              <div className={styles.imgCont}>
                <Image
                  src={mblimageL}
                  alt="R1_with_M1_mobile1"
                  ref={imageL}
                  fill={true}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.imgCont2}>
                <Image
                  src={mblimageR}
                  alt="R1_with_M1_mobile1"
                  ref={imageR}
                  fill={true}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
        </Container>
      </section>
    );
};
